import { Box, Button, Typography } from '@mui/material';
import ResponsiveDialog from 'app/shared-components/layout/dialog/ResponsiveDialog';

export interface StepUpIntroDialogProps {
  open: boolean;
  /** True while the code is being sent (after "Send code"). */
  loading?: boolean;
  /** Supporting text; defaults to the bank-link wording (SEE-221). Flows that
   * reuse the gate for another action (e.g. passkey creation, SEE-213) pass
   * their own so the dialog never promises the wrong follow-up. */
  body?: string;
  onSend: () => void;
  onClose: () => void;
}

const DEFAULT_BODY =
  'For security, we would like to verify your account. After verification you can proceed with linking the bank account.';

// Design tokens — Figma "speedchain_BasicDialog" (node 4255:33966), Material 3.
const COLOR_TITLE = '#3A3B41';
const COLOR_SUPPORT = '#44474F';
const COLOR_PRIMARY = '#193059';
const COLOR_PRIMARY_HOVER = '#142747';
const COLOR_SURFACE = '#E8E7EF';

/**
 * SEE-221: the "Verify account" confirmation shown before the SMS step-up. The
 * code is only sent once the user clicks "Send code" — no SMS fires just from
 * opening the bank-link flow.
 */
function StepUpIntroDialog({
  open,
  loading = false,
  body = DEFAULT_BODY,
  onSend,
  onClose,
}: Readonly<StepUpIntroDialogProps>): JSX.Element {
  return (
    <ResponsiveDialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 360 },
          maxWidth: '100%',
          bgcolor: COLOR_SURFACE,
          borderRadius: '28px',
        },
      }}
    >
      <Box sx={{ p: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Typography
          component="h2"
          sx={{ fontSize: 24, lineHeight: '32px', fontWeight: 400, color: COLOR_TITLE }}
        >
          Verify account
        </Typography>
        <Typography sx={{ fontSize: 14, lineHeight: '20px', letterSpacing: '0.25px', color: COLOR_SUPPORT }}>
          {body}
        </Typography>
        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', mt: '8px' }}
        >
          <Button
            onClick={onClose}
            disabled={loading}
            variant="text"
            sx={{
              color: COLOR_PRIMARY,
              textTransform: 'none',
              fontSize: 14,
              fontWeight: 600,
              borderRadius: '20px',
              px: '12px',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={onSend}
            disabled={loading}
            variant="contained"
            disableElevation
            sx={{
              bgcolor: COLOR_PRIMARY,
              color: '#fff',
              textTransform: 'none',
              fontSize: 14,
              fontWeight: 600,
              borderRadius: '12px',
              px: '24px',
              '&:hover': { bgcolor: COLOR_PRIMARY_HOVER },
            }}
          >
            Send code
          </Button>
        </Box>
      </Box>
    </ResponsiveDialog>
  );
}

export default StepUpIntroDialog;
